/**
 * Centralized constants for affiliate tracking parameters
 */

// Core UTM parameters
export const UTM_PARAMETERS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

// Click ID parameters from various platforms
export const CLICK_ID_PARAMETERS = [
  "gclid", // Google Ads
  "fbclid", // Facebook
  "msclkid", // Microsoft Ads
  "ttclid", // TikTok
  "twclid", // Twitter
  "li_fat_id", // LinkedIn
] as const;

// Generic affiliate parameters
export const AFFILIATE_PARAMETERS = [
  "click_id",
  "affiliate_id",
  "ref",
  "source",
  "referrer",
] as const;

// All supported affiliate tracking parameters
export const ALL_AFFILIATE_PARAMETERS = [
  ...UTM_PARAMETERS,
  ...CLICK_ID_PARAMETERS,
  ...AFFILIATE_PARAMETERS,
] as const;

// Default storage configuration
export const STORAGE_CONFIG = {
  DEFAULT_KEY: "affiliateParams",
  DEFAULT_TTL: 30 * 24 * 60 * 60 * 1000, // 30 days in milliseconds
} as const;

// Attribution models
export const ATTRIBUTION_MODELS = {
  FIRST_TOUCH: "first-touch",
  LAST_TOUCH: "last-touch",
} as const;

// Storage types
export const STORAGE_TYPES = {
  SESSION: "sessionStorage",
  LOCAL: "localStorage",
} as const;

// Event types
export const EVENT_TYPES = {
  CAPTURE: "capture",
  CLEAR: "clear",
  EXPIRE: "expire",
} as const;

// Attribution channels for classification
export const ATTRIBUTION_CHANNELS = {
  DIRECT: "direct",
  PAID_SEARCH: "paid_search",
  ORGANIC_SEARCH: "organic_search",
  SOCIAL: "social",
  EMAIL: "email",
  AFFILIATE: "affiliate",
  REFERRAL: "referral",
  DISPLAY: "display",
} as const;

// Search engines for referrer classification
export const SEARCH_ENGINES = [
  "google.com",
  "bing.com",
  "yahoo.com",
  "duckduckgo.com",
  "baidu.com",
  "yandex.com",
  "ask.com",
] as const;

// Social media platforms for source classification
export const SOCIAL_PLATFORMS = [
  "facebook",
  "instagram",
  "twitter",
  "linkedin",
  "tiktok",
  "youtube",
  "pinterest",
  "snapchat",
] as const;

// Type definitions for the constants
export type UTMParameter = (typeof UTM_PARAMETERS)[number];
export type ClickIdParameter = (typeof CLICK_ID_PARAMETERS)[number];
export type AffiliateParameter = (typeof AFFILIATE_PARAMETERS)[number];
export type AllAffiliateParameter = (typeof ALL_AFFILIATE_PARAMETERS)[number];
export type AttributionModel =
  (typeof ATTRIBUTION_MODELS)[keyof typeof ATTRIBUTION_MODELS];
export type StorageType = (typeof STORAGE_TYPES)[keyof typeof STORAGE_TYPES];
export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];
export type AttributionChannel =
  (typeof ATTRIBUTION_CHANNELS)[keyof typeof ATTRIBUTION_CHANNELS];
